import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, MODULE_LABELS, ROUTE_MODULE, permCan } from "@/lib/constants";
import { PageHeader, StatCard, Loader } from "@/components/ui-kit";
import {
  Users2, Target, FileText, FileSignature, FolderKanban, Clock, Plane,
  ListTodo, TrendingUp, AlertTriangle, CheckCircle2, Banknote, Wallet, HandCoins,
  Play, Coffee, RotateCcw, ArrowRight,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { apiErr } from "@/lib/api";

const QUICK_POINTAGE = {
  not_started: { action: "start_day", label: "Commencer ma journée", icon: Play, color: "bg-brand-600 hover:bg-brand-700" },
  working: { action: "start_break", label: "Prendre une pause", icon: Coffee, color: "bg-amber-500 hover:bg-amber-600" },
  on_break: { action: "resume", label: "Reprendre le travail", icon: RotateCcw, color: "bg-brand-600 hover:bg-brand-700" },
  finished: null,
};

const COLORS = ["#076633", "#10B981", "#0ea5e9", "#FF9F1C", "#EF4444", "#8B5CF6", "#64748B"];

export default function Dashboard() {
  const { user, role, permissions } = useAuth();
  const [data, setData] = useState(null);
  const [treasury, setTreasury] = useState(null);
  const nav = useNavigate();
  const [ptBusy, setPtBusy] = useState(false);

  const quickPointage = async (action) => {
    setPtBusy(true);
    try {
      const { data: res } = await api.post("/attendance/action", { action });
      setData((d) => ({ ...d, pointage_status: res.status }));
      toast.success("Pointage enregistré");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setPtBusy(false); }
  };

  useEffect(() => {
    setData(null);
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  }, [role]);

  useEffect(() => {
    if (role === "caissier") {
      Promise.all([api.get("/cash-accounts"), api.get("/fund-requests")])
        .then(([a, f]) => setTreasury({ accounts: a.data, funds: f.data }))
        .catch(() => setTreasury({ accounts: [], funds: [] }));
    }
  }, [role]);

  if (!data) return <Loader />;

  const isCommercialAdmin = ["super_admin", "direction_generale", "direction_commerciale"].includes(role);
  const isCommercial = role === "commercial";
  const isCollab = ["collaborateur", "responsable"].includes(role);
  const isCaissier = role === "caissier";

  const pipelineData = Object.entries(data.pipeline || {}).map(([name, value]) => ({ name, value }));
  const projStatusData = Object.entries(data.projets_par_statut || {}).map(([name, value]) => ({ name, value }));

  const SHORTCUTS = [
    { to: "/prospects", label: "Prospects" }, { to: "/clients", label: "Clients" },
    { to: "/projets-clients", label: "Projets clients" }, { to: "/opportunites", label: "Opportunités" },
    { to: "/devis", label: "Devis" }, { to: "/prestations", label: "Prestations" },
    { to: "/factures", label: "Factures" }, { to: "/contrats", label: "Contrats" },
    { to: "/modeles-contrats", label: "Modèles de contrats" }, { to: "/livraisons", label: "Livraisons" },
    { to: "/articles", label: "Articles" }, { to: "/projets", label: "Projets internes" },
    { to: "/finance", label: "Finance" }, { to: "/paie", label: "Paie" },
    { to: "/tresorerie", label: "Trésorerie" }, { to: "/messagerie", label: "Messagerie" },
  ];
  const shortcuts = SHORTCUTS.filter((s) => { const m = ROUTE_MODULE[s.to]; return m ? permCan(permissions, m, "read") : true; });

  return (
    <div>
      <PageHeader title={`Bonjour, ${user?.name?.split(" ")[0]} 👋`} subtitle={`Tableau de bord · ${ROLE_LABELS[role]}`} />

      {shortcuts.length > 0 && (
        <div className="mb-6" data-testid="dashboard-shortcuts">
          <p className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-2">Accès rapide aux modules</p>
          <div className="flex flex-wrap gap-2">
            {shortcuts.map((s) => (
              <button key={s.to} onClick={() => nav(s.to)} data-testid={`shortcut-${s.to.replace("/", "")}`}
                className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3.5 py-1.5 text-sm font-medium text-slate-700 hover:border-brand-400 hover:text-brand-700 transition-colors">
                {s.label}<ArrowRight className="h-3.5 w-3.5 opacity-50" />
              </button>
            ))}
          </div>
        </div>
      )}


      {isCommercialAdmin && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Prospects" value={data.prospects ?? 0} icon={Users2} accent="blue" />
            <StatCard label="Opportunités" value={data.opportunites ?? 0} icon={Target} accent="cyan" />
            <StatCard label="Devis" value={data.devis ?? 0} icon={FileText} accent="amber" />
            <StatCard label="Contrats" value={data.contrats ?? 0} icon={FileSignature} accent="emerald" />
            <StatCard label="Projets internes" value={data.projets ?? 0} icon={FolderKanban} accent="blue" />
            <StatCard label="Présents aujourd'hui" value={data.presents ?? 0} icon={CheckCircle2} accent="emerald" />
            <StatCard label="Tâches bloquées" value={data.taches_retard ?? 0} icon={AlertTriangle} accent="rose" />
            <StatCard label="CA signé" value={fmtMoney(data.ca_signe)} icon={Banknote} accent="emerald" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-xl border bg-white p-5">
              <h3 className="font-head font-semibold mb-4">Pipeline commercial</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={pipelineData} margin={{ left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#076633" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-xl border bg-white p-5">
              <h3 className="font-head font-semibold mb-4">Projets par statut</h3>
              {projStatusData.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={projStatusData} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={80} label={(e) => e.name}>
                      {projStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-400">Aucun projet</p>}
            </div>
          </div>
        </>
      )}

      {isCommercial && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Mes prospects" value={data.prospects ?? 0} icon={Users2} accent="blue" />
            <StatCard label="Mes opportunités" value={data.opportunites ?? 0} icon={Target} accent="cyan" />
            <StatCard label="Mes devis" value={data.devis ?? 0} icon={FileText} accent="amber" />
            <StatCard label="Valeur pipeline" value={fmtMoney(data.valeur_pipeline)} icon={TrendingUp} accent="emerald" />
          </div>
          <div className="rounded-xl border bg-white p-5">
            <h3 className="font-head font-semibold mb-4">Mon pipeline</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={pipelineData} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#10B981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {isCollab && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Mes tâches" value={data.mes_taches ?? 0} icon={ListTodo} accent="blue" />
          <StatCard label="En cours" value={data.taches_encours ?? 0} icon={Clock} accent="amber" />
          <StatCard label="Terminées" value={data.taches_terminees ?? 0} icon={CheckCircle2} accent="emerald" />
          <StatCard label="Mes projets" value={data.mes_projets ?? 0} icon={FolderKanban} accent="cyan" />
          <div className="col-span-2 lg:col-span-2 rounded-xl border bg-white p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase font-mono text-slate-400">Pointage du jour</p>
                <p className="font-head text-lg font-bold mt-1 text-slate-800">
                  {({ not_started: "Non commencé", working: "En activité", on_break: "En pause", finished: "Journée terminée" })[data.pointage_status] || "—"}
                </p>
              </div>
              <span className={`h-3 w-3 rounded-full ${data.pointage_status === "working" ? "bg-emerald-500 animate-pulse" : data.pointage_status === "on_break" ? "bg-amber-500" : data.pointage_status === "finished" ? "bg-slate-400" : "bg-slate-300"}`} />
            </div>
            {(() => {
              const q = QUICK_POINTAGE[data.pointage_status || "not_started"];
              return q ? (
                <button
                  onClick={() => quickPointage(q.action)}
                  disabled={ptBusy}
                  data-testid="quick-pointage-btn"
                  className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl py-3 text-white font-semibold transition-transform active:scale-95 disabled:opacity-60 ${q.color}`}
                >
                  <q.icon className="h-5 w-5" />{q.label}
                </button>
              ) : (
                <p className="mt-4 text-sm text-slate-500 text-center py-2">Journée terminée. À demain 👋</p>
              );
            })()}
            <button onClick={() => nav("/pointage")} data-testid="open-pointage" className="mt-2 flex w-full items-center justify-center gap-1 text-xs font-semibold text-brand-600 hover:underline">
              Ouvrir le pointage complet <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <StatCard label="Congés en attente" value={data.conges_attente ?? 0} icon={Plane} accent="amber" />
        </div>
      )}

      {isCaissier && (
        treasury === null ? null : (() => {
          const total = treasury.accounts.reduce((s, a) => s + (a.solde || 0), 0);
          const toDisburse = treasury.funds.filter((f) => f.statut === "Approuvé");
          const pending = treasury.funds.filter((f) => f.statut === "En attente");
          return (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard label="Solde total caisses" value={fmtMoney(total)} icon={Wallet} accent="emerald" />
                <StatCard label="Nombre de caisses" value={treasury.accounts.length} icon={Banknote} accent="blue" />
                <StatCard label="À décaisser" value={toDisburse.length} icon={HandCoins} accent="amber" hint="Demandes approuvées" />
                <StatCard label="En attente validation" value={pending.length} icon={Clock} accent="slate" />
              </div>
              <div className="rounded-xl border bg-white p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-head font-semibold">Demandes à décaisser</h3>
                  <button onClick={() => nav("/demandes-fonds")} className="text-sm font-semibold text-brand-600 hover:underline">Voir tout</button>
                </div>
                {toDisburse.length === 0 ? <p className="text-sm text-slate-400 py-4">Aucune demande approuvée en attente de décaissement.</p> : (
                  <div className="space-y-2">
                    {toDisburse.map((f) => (
                      <button key={f.id} onClick={() => nav("/demandes-fonds")} className="flex w-full items-center justify-between rounded-lg border p-3 hover:border-brand-400 text-left">
                        <div><p className="font-semibold text-sm">{f.user_name} <span className="font-mono text-xs text-slate-400">{f.numero}</span></p><p className="text-xs text-slate-500">{f.motif}</p></div>
                        <span className="font-mono font-bold text-brand-600">{fmtMoney(f.montant)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          );
        })()
      )}
    </div>
  );
}
